<template>
  <div v-if="canShow">
    <div v-if="updatingTour" class="loader-tour-container">
      <v-loader />
    </div>

    <div v-if="status === 'loaded'" :class="{ 'opacity-50': updatingTour }">
      <div class="d-flex justify-content-end mb-8 gap-8">
        <!-- Any additional buttons can go here to match Case Builder pattern -->
      </div>

      <div class="w-100 d-flex justify-content-start mb-8 ms-12">
        <v-button
          :disabled="updatingTour"
          class="btn-add-plus rounded-circle shadow-sm"
          size="xs"
          variant="primary"
          @click="addTemplateDayAtFirst()"
        >
          <v-icon icon="plus" style="font-size: 10px; line-height: 1;" />
        </v-button>
      </div>
      <div class="tour-container">
        <the-template-day-draggable-v2
          v-model="tourDays"
          :agency="authStore.agency?.slug ?? ''"
          :current-tour-day="currentTemplateDayId ?? ''"
          :template-id="Number(template.id)"
          :tour-mapped="tourMappedOriginal"
          :updating-tour="updatingTour"
          class="mb-16"
          type="tour-day"
          @day-moved="updateTemplateDay"
          @emit-add-day="handleEmitAddDay"
          @reload-tour="refreshCurrentTemplate"
          @open-item-details-off-canvas="openItemDetailsOffCanvas"
          @open-day-information-off-canvas="openDayInformationOffCanvas"
          @move-item-to-another-day="moveItemToAnotherDay"
        />
      </div>

      <div class="w-100 d-flex justify-content-center mt-8">
        <v-button
          :disabled="updatingTour"
          class="btn-add-plus rounded-circle shadow-sm"
          size="xs"
          variant="primary"
          @click="addTemplateDayAtEnd()"
        >
          <v-icon icon="plus" style="font-size: 10px; line-height: 1;" />
        </v-button>
      </div>
    </div>

    <v-loader v-else />

    <the-template-day-item-details-off-canvas-v2
      v-if="offCanvasToShow === 'the-template-day-item-details-off-canvas'"
      :agency="authStore.agency?.slug ?? ''"
      :status="statusTourDayItem"
      :template-id="Number(template.id)"
      @closed="
        closeOffCanvas();
        clearCurrentTourDayItem();
      "
      @emit-reload-tour="refreshCurrentTemplate"
    />
    <the-template-day-information-off-canvas
      v-if="
        offCanvasToShow === 'the-template-day-information-off-canvas' &&
        !!currentTemplateDayId &&
        !!currentTemplateDayStage
      "
      :agency="authStore.agency?.slug ?? ''"
      :template-day-id="currentTemplateDayId"
      :template-day-stage="currentTemplateDayStage"
      :template-id="Number(template.id)"
      @closed="
        closeOffCanvas();
        currentTemplateDayId = null;
        currentTemplateDayStage = null;
      "
      @emit-reload-template="refreshCurrentTemplate"
    />
  </div>

  <the-screen-size-verify @can-show="canShow = $event" />
</template>

<script lang="ts" setup>
import { getCurrentInstance, onMounted,ref } from "vue";
import { DateTime } from "luxon";
import { TYPE, useToast } from "vue-toastification";
import api from "@/api";
import VLoader from "@/components/vendor/loader/VLoader.vue";
import VIcon from "@/components/vendor/basic/icon/VIcon.vue";
import VButton from "@/components/vendor/basic/button/VButton.vue";
import { useAuthStore } from "@/stores/auth";
import useOffCanvasUtils from "@/helpers/OffCanvasUtils";
import { useUserStore } from "@/stores/user";
import Template from "@/api/objects/Template";
import TheTemplateDayDraggableV2 from "@/components/template/V2/TheTemplateDayDraggableV2.vue";
import TheTemplateDayItemDetailsOffCanvasV2 from "@/components/template/V2/TheTemplateDayItemDetailsOffCanvasV2.vue";
import TourDayItemResource from "@/api/objects/TourDayItemResource";
import TheTemplateDayInformationOffCanvas from "@/components/template/TheTemplateDayInformationOffCanvas.vue";
import TheScreenSizeVerify from "@/components/vendor/TheScreenSizeVerify.vue";
import { useI18n } from "vue-i18n";

const props = defineProps({
  template: {
    type: Object as () => Template,
    required: true,
  },
});

const emit = defineEmits(["reloadTemplate"]);
const toast = useToast();
const instance = getCurrentInstance();
const $modal = instance?.appContext.config.globalProperties.$modal;
const $toast = instance?.appContext.config.globalProperties.$toast;
const { t } = useI18n();
const authStore = useAuthStore();
const userStore = useUserStore();
const { offCanvasToShow, openOffCanvas, closeOffCanvas } = useOffCanvasUtils();

const canShow = ref(false);
const statusTourDayItem = ref<string>("loading");
const tourMapped = ref<Record<string, any>>({});
const tourMappedOriginal = ref<Record<string, any>>({});
const tourDays = ref<Record<string, any>[]>([]);
const status = ref<string>("loading");
const updatingTour = ref<boolean>(false);
const templateName = ref<string>("");
const templateDates = ref<string[]>([]);
const currentTemplateDayId = ref<number | null>(null);
const currentTemplateDayStage = ref<number | null>(null);
const isCollaborator = ref<boolean>(false);

const agencySlug = authStore.agency?.slug ?? '';
const currentTemplate: any = userStore.currentTemplate ?? {};
const currentTemplateId = currentTemplate.id ? Number(currentTemplate.id) : 0;

const loadTour = async () => {
  updatingTour.value = true;
  templateDates.value = [];
  tourMapped.value = {};
  tourMappedOriginal.value = {};

  if (userStore.currentTemplate) {
    isCollaborator.value = userStore.currentTemplate?.isCollaborator;
    userStore.currentTemplate.tourDays?.forEach((tourDay: Record<string, any>) => {
      templateDates.value.push(tourDay.date);
    });

    templateName.value = userStore.currentTemplate?.name;

    await mapTour(userStore.currentTemplate);
  }
  updatingTour.value = false;
};

const mapTour = (tour: Record<string, any>) => {
  const tourDaysItemsHotelCache: Record<string, any>[] = [];
  
  const mappedTourDays = tour?.tourDays?.map((tourDay: Record<string, any>) => {
    const tourDayItems = tourDay?.activityDayItems
      ?.map((tourDayItem: Record<string, any>) => {
        let supplierName = "";

        if (tourDayItem?.model !== undefined) {
          const defaultSupplierName = `(${t("general.shared.supplierNotFound")})`;
          const resourceName = tourDayItem.model?.name;
          supplierName = resourceName || defaultSupplierName;
        }

        const tourDayItemMapped: Record<string, any> = {
          id: tourDayItem.id,
          ...tourDayItem,
          disabled: false,
          supplier: supplierName,
        };

        if (tourDayItem.type.toLowerCase() === "hotel" && !tourDayItem.disabled) {
          const nights = tourDayItem.nights || 1;
          
          if (nights > 1) {
            for (let i = 1; i < nights; i++) {
              const nextDayOrder = tourDay.order + i;
              const autoGeneratedItem = {
                ...tourDayItemMapped,
                id: `${tourDayItem.id}_auto_${i}`, 
                targetDayOrder: nextDayOrder, 
                disabled: true,
                isAutoGenerated: true, 
                originalHotelId: tourDayItem.id,
              };
              tourDaysItemsHotelCache.push(autoGeneratedItem);
            }
          }
        }

        return tourDayItemMapped;
      })
      .sort((itemA: Record<string, any>, itemB: Record<string, any>) => itemA.position - itemB.position);

      
      const tourDayIsBlocked = tourDay?.activityDayItems?.some((tourDayItem: Record<string, any>) => !!tourDayItem.isBlocked);
      
      if (!tourDay.stageLength) {
        tourDay.stageLength = 0;
      }
      
      return {
        id: tourDay.id,
        ...tourDay,
        isStage: !!(tourDay.isStage ?? tourDay.is_stage),
        originId: tourDay.originId ?? tourDay.origin_id ?? null,
        destinationId: tourDay.destinationId ?? tourDay.destination_id ?? null,
        isBlocked: tourDayIsBlocked,
        items: tourDayItems || [],
      };
    });
    
    
    
    tourDaysItemsHotelCache.forEach((tourDayItemHotel: Record<string, any>) => {
      const tourDayCheckOut = mappedTourDays.find(
        (tourDayAux: Record<string, any>) => tourDayAux.order === tourDayItemHotel.targetDayOrder
    );

    if (tourDayCheckOut) {
      tourDayItemHotel.disabled = true;
      tourDayCheckOut.items.unshift(tourDayItemHotel);
    }
  });
  
  tourMapped.value = {
    id: tour.id,
    ...tour,
    tourDays: mappedTourDays,
    client: tour.client,
  };

  
  tourDays.value = tourMapped.value.tourDays;
  tourMappedOriginal.value = JSON.parse(JSON.stringify(tourMapped.value));

  status.value = "loaded";
};

const moveItemToAnotherDay = async (item: Record<string, any>) => {
  const newTourDay = tourDays.value.find((tourDay) =>
    tourDay.items.some((tourDayItem: Record<string, any>) => tourDayItem.id === item.element.id)
  );

  if (authStore.agency && newTourDay) {
    try {
      await api.activityService.moveActivityDayItem(
        authStore.agency.slug,
        tourMapped.value.id,
        newTourDay.id,
        item.element.id,
        item.newPosition
      );

      toast.success(t("general.shared.savedChanges"));

      await refreshCurrentTemplate();
    } catch (e: any) {
      toast.error(e.response?.data?.message || "An error occurred");

      await refreshCurrentTemplate();
    }
  }
};

const updateTemplateDay = async (tourDay: Record<string, any>) => {
  if (authStore.agency) {
    await api.activityService.updateTourDay(authStore.agency.slug, tourMapped.value.id, tourDay.id, {
      is_stage: !!tourDay.isStage,
      order: tourDay.order,
      origin: tourDay.originId ?? tourDay.origin_id,
      destination: tourDay.destinationId ?? tourDay.destination_id,
    });
  }

  await refreshCurrentTemplate();
};

const addTemplateDay = async (order?: number, type?: string) => {
  const agencySlug = authStore.agency?.slug ?? '';
  const activityId = Number(tourMapped.value?.id ?? 0);
  if (!agencySlug || !activityId || !$modal || !$toast) {
    console.error('Missing required context for creating a day.');
    return;
  }

  const newOrder = order ?? (tourDays.value.length + 1);
  const newType = type ?? 'rest';

  await $modal.confirm({
    title: t("tourBuilder.addDayTitle"),
    text: t("tourBuilder.addDayText"),
    leftButtonText: t("general.button.cancel"),
    switchButtons: true,
    confirmButtonCallback: async () => {
      const loadingToast = $toast.loading(t("general.button.adding"), "toast-add-template-day");

      try {
        const newDayPayload = {
          order: newOrder,
          type: newType
        };

        await api.activityService.createDay(
          agencySlug,
          activityId,
          newDayPayload
        );

        await refreshCurrentTemplate();

        loadingToast.update("toast-add-template-day", {
          content: t("tourBuilder.dayCreated"),
          options: {
            type: TYPE.SUCCESS,
            icon: "icon icon-check-circle-fill",
            timeout: 5000,
          },
        });
      } catch (e: any) {
        console.error(e.response?.data);

        loadingToast.update("toast-add-template-day", {
          content: t("tourBuilder.dayCreateError"),
          options: {
            type: TYPE.ERROR,
            icon: "icon icon-exclamation-triangle-fill",
            timeout: 5000,
          },
        });
      }
    },
    confirmButtonLoadingText: t("general.button.adding"),
    confirmButtonText: t("general.button.add"),
  });
};

const addTemplateDayAtFirst = () => {
  addTemplateDay(1, 'rest');
};

const addTemplateDayAtEnd = () => {
  addTemplateDay(tourDays.value.length + 1, 'rest');
};

const handleEmitAddDay = (index: number) => {
  addTemplateDay(index + 1, 'rest');
};

const openItemDetailsOffCanvas = async (data: Record<string, any>) => {
  statusTourDayItem.value = "loading";

  if (offCanvasToShow.value !== "the-template-day-item-details-off-canvas") {
    openOffCanvas("the-template-day-item-details-off-canvas");
  }

  await userStore.setCurrentTemplateDayItemV2(data);

  statusTourDayItem.value = "loaded";
};

const openDayInformationOffCanvas = async (templateDayId: number, templateDayStage: number) => {
  currentTemplateDayId.value = templateDayId;
  currentTemplateDayStage.value = templateDayStage;

  if (offCanvasToShow.value !== "the-template-day-information-off-canvas") {
    openOffCanvas("the-template-day-information-off-canvas");
  }
};

const clearCurrentTourDayItem = () => {
  userStore.setCurrentTourDayItem(null);
};

const refreshCurrentTemplate = async () => {
  updatingTour.value = true;

  const tourData = {
    id: userStore.currentTemplate?.id,
    agency: authStore.agency?.slug,
  };

  await userStore.setCurrentTemplateV2(tourData);
  await loadTour();

  updatingTour.value = false;
};

// watch(templateId, async (newValue, oldValue) => {
//   if (newValue !== oldValue) {
//     await loadTour();
//   }
// });

const updateHotelGroupNights = async (updatedHotel: any, newNights: number) => {
  if (!authStore.agency) return;
  
  try {
    // Find all hotels with the same supplier/model across all days
    const allTourDays = tourMapped.value?.tourDays || [];
    
    for (const tourDay of allTourDays) {
      for (const dayItem of tourDay.items || []) {
        // Check if this is a hotel with the same supplier/model as the updated one
        if (dayItem.type?.toLowerCase() === 'hotel' && 
            !dayItem.isAutoGenerated &&
            dayItem.modelId === updatedHotel.modelId && 
            dayItem.id !== updatedHotel.id) {
          
          // Update this hotel with the same nights count
          await api.activityService.updateDayItem(
            authStore.agency.slug,
            tourMapped.value.id,
            dayItem.activityDayId,
            dayItem.id,
            { nights: newNights }
          );
        }
      }
    }
  } catch (e: any) {
    console.error('Error updating hotel group nights:', e);
  }
};

onMounted(async () => {
  await loadTour();
});
</script>

<style lang="scss" scoped>
.btn-add-plus {
  width: 20px !important;
  height: 20px !important;
  min-width: 20px !important;
  min-height: 20px !important;
  padding: 0 !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  border: 0 !important;
}

.tour-steps-container {
  gap: 1rem;
}

.list-enter-from {
  opacity: 0;
  transform: scaleY(0.25);
}

.list-enter-active,
.list-leave-active {
  transition: all 1s cubic-bezier(0.55, 0, 0.1, 1);
}

.list-leave-to {
  opacity: 0;
  transform: scaleY(0);
}

.list-move {
  transition: all 1s cubic-bezier(0.55, 0, 0.1, 1);
}

.list-leave-active {
  position: absolute;
}

:deep(.form-control),
:deep(.vs__selected),
:deep(.vs__dropdown-option) {
  font-size: 0.9375rem !important;
}

:deep(.v-form-label),
:deep(.form-check-label) {
  font-size: 0.8125rem !important;
}
</style>
